import React, { createContext, useContext, useMemo, useState } from 'react';

type PanelState = {
	isOpen: boolean;
	content: React.ReactNode | null;
	owner?: string;
};

type OpenArgs = {
	content: React.ReactNode;
	owner?: string;
};

export type PanelController = {
	isOpen: boolean;
	content: React.ReactNode | null;
	open: (args: OpenArgs) => void;
	close: () => void;
	toggle: (args: OpenArgs) => void;
	setContent: (content: React.ReactNode, owner?: string) => void;
};

const PanelContext = createContext<PanelController | undefined>(undefined);

export const PanelProvider = ({ children }: { children: React.ReactNode }): React.JSX.Element => {
	const [state, setState] = useState<PanelState>({ isOpen: false, content: null });

	const controller = useMemo<PanelController>(
		() => ({
			isOpen: state.isOpen,
			content: state.content,
			open: ({ content, owner }: OpenArgs) => setState({ isOpen: true, content, owner }),
			close: () => setState((s) => ({ ...s, isOpen: false })),
			toggle: ({ content, owner }: OpenArgs) =>
				setState((s) => ({ isOpen: !s.isOpen, content: !s.isOpen ? content : s.content, owner })),
			setContent: (content: React.ReactNode, owner?: string) =>
				setState((s) => ({ ...s, content, owner })),
		}),
		[state.isOpen, state.content],
	);

	return <PanelContext.Provider value={controller}>{children}</PanelContext.Provider>;
};

export const usePanelController = (): PanelController => {
	const ctx = useContext(PanelContext);
	if (!ctx) throw new Error('usePanelController must be used within PanelProvider');
	return ctx;
};
